import { FC, ReactElement, ReactNode } from "react";
import {
  chakra,
  Dialog as ChakraDialog,
  DialogRootProps,
  Portal,
  Flex,
  CloseButton,
} from "@chakra-ui/react";
import {
  dialogHeaderRecipe,
  dialogContentRecipe,
  dialogBodyRecipe,
} from "./dialog.recipe";

type Props = {
  trigger?: ReactElement;
  header?: string;
  subHeader?: ReactNode;
  actions?: ReactElement[];
  autoHeight?: boolean;
  maxWidth?: number;
  onClose?: VoidFunction;
} & DialogRootProps;

export const DialogRoot = ChakraDialog.Root;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogHeader = chakra(ChakraDialog.Header, dialogHeaderRecipe);
export const DialogBody = chakra(ChakraDialog.Body, dialogBodyRecipe);
export const DialogContent = chakra(ChakraDialog.Content, dialogContentRecipe);
export const DialogFooter = ChakraDialog.Footer;
export const DialogPositioner = ChakraDialog.Positioner;
export const DialogCloseTrigger = ChakraDialog.CloseTrigger;

const Dialog: FC<Props> = ({
  trigger,
  header,
  subHeader,
  actions = [],
  children,
  autoHeight,
  maxWidth,
  ...props
}) => (
  <DialogRoot
    scrollBehavior="inside"
    closeOnInteractOutside
    closeOnEscape
    {...props}
  >
    {trigger ? (
      <DialogTrigger asChild>{trigger}</DialogTrigger>
    ) : (
      <DialogTrigger />
    )}

    <Portal>
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent
          height={autoHeight ? "auto" : undefined}
          maxWidth={maxWidth}
        >
          <DialogHeader>
            <DialogCloseTrigger asChild>
              <CloseButton onClick={props.onClose} />
            </DialogCloseTrigger>

            <div>
              {header && <h2>{header}</h2>}
              {subHeader}
            </div>
          </DialogHeader>

          <DialogBody visual={header ? undefined : "withoutTitle"}>
            {children}
          </DialogBody>

          {Boolean(actions.length) && (
            <DialogFooter>
              <Flex gap={2}>{actions}</Flex>
            </DialogFooter>
          )}
        </DialogContent>
      </DialogPositioner>
    </Portal>
  </DialogRoot>
);

export default Dialog;
